// components/workspace-timeline/useWorkspaceTimelineTrackProjection.ts
import React, { useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { isLayerVectorized } from '#lib/svg-layer';
import { getDrawingAwareSvgLayerSource } from '#lib/drawing-layer';
import type { PendingVideoGenerationTrack } from '#lib/timeline-generation-placeholders';
import {
  buildWorkspaceTimelineTrackDataSignature,
  buildWorkspaceTimelineTracks,
  resolveWorkspaceTimelineActiveTrackId,
  resolveWorkspaceTimelineDisplayTracks,
  resolveWorkspaceTimelineDurationMetrics,
  MIN_TIMELINE_TRACK_SPAN_MS,
  resolveWorkspaceTimelineLaneRows,
  resolveWorkspaceTimelineRange,
  resolveWorkspaceTimelineResolvedTracks,
  toFiniteTimelineMs as toFinite,
  type WorkspaceTimelineLayerSource,
  type WorkspaceTimelineResolvedTrackView,
  type WorkspaceTimelineTrackView,
} from '#lib/workspace-timeline';
import {
  TIMELINE_ID_SIGNATURE_SEPARATOR,
  buildIdSignature,
  buildScopedMembershipSignature,
  clamp,
  getWorkspaceTimelineTrackSource as getTrackSource,
  timelineDropPreviewFill,
} from './presentation';
import type {
  ResolvedWorkspaceTimelineTrack,
  TimelineFileDropPreview,
  TimelineLaneRow,
  TrackDragTimingPreview,
  TrackLaneDragPreview,
  WorkspaceTimelineLanePresentationRow,
  WorkspaceTimelineTrack,
} from './types';

const MIN_TRACK_SPAN_MS = MIN_TIMELINE_TRACK_SPAN_MS;
const EMPTY_TIMELINE_TRACKS: WorkspaceTimelineTrack[] = [];
const EMPTY_TRACK_LAYER_IDS: string[] = [];
const TIMELINE_DROP_PLACEHOLDER_DURATION_RATIO = 0.2;

const resolveUiTimelineImageSrc = (
  layer: WorkspaceTimelineLayerSource,
): string | null => {
  if (isLayerVectorized(layer)) {
    return getDrawingAwareSvgLayerSource({
      imageSrc: typeof layer.imageSrc === 'string' ? layer.imageSrc : undefined,
      params: layer.params ?? undefined,
    });
  }
  return typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : null;
};

const toUiTimelineTrack = (
  track: WorkspaceTimelineTrackView,
  layerRegistry: Record<string, WorkspaceTimelineLayerSource | undefined>
): WorkspaceTimelineTrack => {
  const layer = layerRegistry[track.layerId];
  return {
    layerId: track.layerId,
    name: track.name,
    layerType: track.layerType,
    isLayerVisible: track.visible !== false,
    videoSrc: track.videoSrc,
    imageSrc: track.imageSrc,
    audioSrc: track.audioSrc,
    separatedFromLayerId: track.separatedFromLayerId,
    audioGain: track.audioGain,
    mutedInTimeline: track.mutedInTimeline,
    renderOrder: track.renderOrder,
    startMs: track.startMs,
    endMs: track.endMs,
    sourceInMs: track.sourceInMs,
    playbackRate: track.playbackRate,
    hasExplicitTimelineTiming: toFinite(layer?.timelineStartMs) !== null || toFinite(layer?.timelineEndMs) !== null,
    fadeInRatio: track.fadeInRatio,
    fadeOutRatio: track.fadeOutRatio,
    rotationDeg: track.rotationDeg,
    flipX: track.flipX,
    flipY: track.flipY,
    opacity: track.opacity,
    laneId: track.laneId,
    laneOrder: track.laneOrder,
    rect: { ...track.rect },
    scale: track.scale,
  };
};

const toUiResolvedTimelineTrack = (
  track: WorkspaceTimelineResolvedTrackView,
): ResolvedWorkspaceTimelineTrack => ({
  layerId: track.layerId,
  name: track.name,
  layerType: track.layerType,
  isLayerVisible: track.visible !== false,
  videoSrc: track.videoSrc,
  imageSrc: track.imageSrc,
  audioSrc: track.audioSrc,
  separatedFromLayerId: track.separatedFromLayerId,
  audioGain: track.audioGain,
  mutedInTimeline: track.mutedInTimeline,
  renderOrder: track.renderOrder,
  startMs: track.startMs,
  endMs: track.endMs,
  sourceInMs: track.sourceInMs,
  sourceOutMs: track.sourceOutMs,
  activeDurationMs: track.activeDurationMs,
  mediaDurationMs: track.mediaDurationMs,
  trackLimitEndMs: track.trackLimitEndMs,
  minSegmentMs: track.minSegmentMs,
  fadeInRatio: track.fadeInRatio,
  fadeOutRatio: track.fadeOutRatio,
  playbackRate: track.playbackRate,
  rotationDeg: track.rotationDeg,
  flipX: track.flipX,
  flipY: track.flipY,
  opacity: track.opacity,
  fadeInEndMs: track.fadeInEndMs,
  fadeOutStartMs: track.fadeOutStartMs,
  laneId: track.laneId,
  laneOrder: track.laneOrder,
  rect: { ...track.rect },
  scale: track.scale,
});

const toCanonicalTimelineTrackView = (
  track: WorkspaceTimelineTrack,
): WorkspaceTimelineTrackView => ({
  layerId: track.layerId,
  layerType: track.layerType,
  name: track.name,
  visible: track.isLayerVisible !== false,
  renderOrder: track.renderOrder,
  topLayer: false,
  startMs: track.startMs,
  endMs: track.endMs,
  sourceInMs: track.sourceInMs,
  playbackRate: track.playbackRate,
  rect: track.rect,
  scale: track.scale,
  rotationDeg: track.rotationDeg ?? 0,
  flipX: track.flipX === true,
  flipY: track.flipY === true,
  opacity: typeof track.opacity === 'number' && Number.isFinite(track.opacity) ? track.opacity : 1,
  imageSrc: track.imageSrc,
  videoSrc: track.videoSrc,
  audioSrc: track.audioSrc,
  mutedInTimeline: track.mutedInTimeline,
  audioGain: track.audioGain,
  separatedFromLayerId: track.separatedFromLayerId ?? null,
  fadeInRatio: track.fadeInRatio ?? 0,
  fadeOutRatio: track.fadeOutRatio ?? 0,
  laneId: track.laneId ?? track.layerId,
  laneOrder: track.laneOrder ?? track.renderOrder,
});

const toCanonicalResolvedTimelineTrackView = (
  track: ResolvedWorkspaceTimelineTrack,
): WorkspaceTimelineResolvedTrackView => ({
  ...toCanonicalTimelineTrackView(track),
  endMs: track.endMs,
  sourceOutMs: track.sourceOutMs,
  activeDurationMs: track.activeDurationMs,
  mediaDurationMs: track.mediaDurationMs,
  trackLimitEndMs: track.trackLimitEndMs,
  minSegmentMs: track.minSegmentMs,
  fadeInRatio: track.fadeInRatio,
  fadeOutRatio: track.fadeOutRatio,
  laneId: track.laneId,
  laneOrder: track.laneOrder,
  fadeInEndMs: track.fadeInEndMs,
  fadeOutStartMs: track.fadeOutStartMs,
});

export interface UseWorkspaceTimelineTrackProjectionArgs {
  projectId: string;
  workspaceId: string;
  cursorMs: number;
  persistedDurationMs: number | null;
  pendingVideoGenerationTracks: PendingVideoGenerationTrack[];
  mediaDurationVersion: number;
  probedTrackDurationsRef: React.MutableRefObject<Map<string, { src: string; durationMs: number }>>;
  separatingSoundTrackIds: string[];
  mergingTrackIds: string[];
  activeDragTrackId: string | null;
  activeTrackOrderDragId: string | null;
  activeTrackDragInitialLaneId: string | null;
  splitHoverTrackId: string | null;
  splitPreview: { trackId: string; splitMs: number } | null;
  splitScissorPulseFrame: boolean;
  trackDragTimingPreview: TrackDragTimingPreview | null;
  trackLaneDragPreview: TrackLaneDragPreview | null;
  timelineFileDropPreview: TimelineFileDropPreview | null;
}

export interface UseWorkspaceTimelineTrackProjectionResult {
  timelineLayerIdsSignature: string;
  selectedLayerIdSet: Set<string>;
  separatingSoundTrackIdSet: Set<string>;
  mergingTrackIdSet: Set<string>;
  tracks: WorkspaceTimelineTrack[];
  maxDeclaredTrackEndMs: number;
  durationMs: number;
  resolvedTracks: ResolvedWorkspaceTimelineTrack[];
  visibleResolvedTracks: ResolvedWorkspaceTimelineTrack[];
  previewResolvedTracks: WorkspaceTimelineTrack[];
  laneRows: TimelineLaneRow[];
  displayTrackOrderLayerIds: string[];
  maxLaneOrder: number;
  timelineLanePresentationRows: WorkspaceTimelineLanePresentationRow[];
  hasAnyTrack: boolean;
  hasPlayableMediaLayers: boolean;
  hasPlayableMediaTrack: boolean;
  clampedCursorMs: number;
  playbackStartMs: number;
  playbackEndMs: number;
  rulerRangeStartMs: number;
  rulerRangeEndMs: number;
  rulerRangeMs: number;
  timelineDropPlaceholderDurationMs: number;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeEndMs: number;
}

export const useWorkspaceTimelineTrackProjection = ({
  projectId,
  workspaceId,
  cursorMs,
  persistedDurationMs,
  pendingVideoGenerationTracks,
  mediaDurationVersion,
  probedTrackDurationsRef,
  separatingSoundTrackIds,
  mergingTrackIds,
  activeDragTrackId,
  activeTrackOrderDragId,
  activeTrackDragInitialLaneId,
  splitHoverTrackId,
  splitPreview,
  splitScissorPulseFrame,
  trackDragTimingPreview,
  trackLaneDragPreview,
  timelineFileDropPreview,
}: UseWorkspaceTimelineTrackProjectionArgs): UseWorkspaceTimelineTrackProjectionResult => {
  const { kernel } = useKernel();
  const timelineLayerIdsSignature = useKernelStore((state) => {
    const layerIds = state.inventory.StudioWorkspace.registry[workspaceId]?.layerIds;
    return Array.isArray(layerIds)
      ? layerIds
          .filter((layerId): layerId is string => typeof layerId === 'string' && layerId.length > 0)
          .join(TIMELINE_ID_SIGNATURE_SEPARATOR)
      : '';
  });
  const timelineLayerIds = useMemo(() => {
    if (!timelineLayerIdsSignature) return EMPTY_TRACK_LAYER_IDS;
    return timelineLayerIdsSignature.split(TIMELINE_ID_SIGNATURE_SEPARATOR);
  }, [timelineLayerIdsSignature]);
  const timelineTrackDataSignature = useKernelStore((state) => {
    const workspace = state.inventory.StudioWorkspace.registry[workspaceId];
    const layerIds = Array.isArray(workspace?.layerIds)
      ? workspace.layerIds.filter((layerId): layerId is string => typeof layerId === 'string' && layerId.length > 0)
      : EMPTY_TRACK_LAYER_IDS;

    return buildWorkspaceTimelineTrackDataSignature(
      layerIds,
      (layerId) => state.inventory.StudioLayer['registry'][layerId]
    );
  });
  const selectedLayerIdsSignature = useKernelStore((state) => (
    buildIdSignature(state.inventory.Project.registry[projectId]?.runtime?.selectedLayersId)
  ));
  const selectedLayerIdSet = useMemo(() => {
    if (!selectedLayerIdsSignature) return new Set<string>();
    return new Set(selectedLayerIdsSignature.split(TIMELINE_ID_SIGNATURE_SEPARATOR));
  }, [selectedLayerIdsSignature]);
  const separatingSoundTrackIdsSignature = useMemo(
    () => buildIdSignature(separatingSoundTrackIds),
    [separatingSoundTrackIds]
  );
  const mergingTrackIdsSignature = useMemo(
    () => buildIdSignature(mergingTrackIds),
    [mergingTrackIds]
  );
  const separatingSoundTrackIdSet = useMemo(
    () => new Set(separatingSoundTrackIds),
    [separatingSoundTrackIds]
  );
  const mergingTrackIdSet = useMemo(
    () => new Set(mergingTrackIds),
    [mergingTrackIds]
  );
  const tracks = useMemo(() => {
    const layerRegistry = kernel.current?.getLayerRegistry() as Record<string, WorkspaceTimelineLayerSource | undefined> | undefined;
    if (!layerRegistry || timelineLayerIds.length === 0) return EMPTY_TIMELINE_TRACKS;
    return buildWorkspaceTimelineTracks({
      workspaceId,
      layerIds: timelineLayerIds,
      layerRegistry,
      options: {
        roundTiming: false,
        resolveImageSrc: (layer, _layerId, layerType) => (
          layerType === 'image' ? resolveUiTimelineImageSrc(layer) : null
        ),
      },
    }).map((track) => toUiTimelineTrack(track, layerRegistry));
  }, [kernel, timelineLayerIds, timelineTrackDataSignature, workspaceId]);
  const previewTracks = useMemo<WorkspaceTimelineTrack[]>(() => {
    if (!trackDragTimingPreview) return tracks;
    let hasPreviewedTrack = false;
    const nextTracks = tracks.map((track) => {
      const snapshot = trackDragTimingPreview.snapshotsByLayerId[track.layerId];
      const isEffectTrack = track.layerId === trackDragTimingPreview.layerId;
      if (!snapshot && !isEffectTrack) return track;

      hasPreviewedTrack = true;
      return {
        ...track,
        ...(snapshot ? {
          startMs: snapshot.startMs,
          endMs: snapshot.endMs,
          sourceInMs: snapshot.sourceInMs,
          hasExplicitTimelineTiming: true,
        } : {}),
        ...(isEffectTrack && trackDragTimingPreview.fadeInRatio !== null
          ? { fadeInRatio: trackDragTimingPreview.fadeInRatio }
          : {}),
        ...(isEffectTrack && trackDragTimingPreview.fadeOutRatio !== null
          ? { fadeOutRatio: trackDragTimingPreview.fadeOutRatio }
          : {}),
      };
    });
    return hasPreviewedTrack ? nextTracks : tracks;
  }, [trackDragTimingPreview, tracks]);
  const hasPlayableMediaLayers = useMemo(
    () => tracks.some((track) => track.layerType === 'video' || track.layerType === 'audio'),
    [tracks]
  );
  // Durations live on the video elements and the probe cache; mediaDurationVersion re-runs this when they load.
  /* eslint-disable react-hooks/refs */
  const trackMediaDurationById = useMemo(() => {
    const mediaDurationRevision = mediaDurationVersion;
    const map = new Map<string, number>();
    const videoRefs = kernel.current?.videoRefs.current;
    tracks.forEach((track) => {
      if (track.layerType !== 'video' && track.layerType !== 'audio') return;
      const normalizedTrackSrc = getTrackSource(track);
      const videoElement = videoRefs?.[track.layerId];
      let resolvedDurationMs: number | null = null;
      if (track.layerType === 'video' && videoElement) {
        const mediaDurationSec = videoElement.duration;
        if (Number.isFinite(mediaDurationSec) && mediaDurationSec > 0) {
          resolvedDurationMs = Math.max(1, mediaDurationSec * 1000);
        }
      }
      if (resolvedDurationMs === null && normalizedTrackSrc.length > 0) {
        const probedDuration = probedTrackDurationsRef.current.get(track.layerId);
        if (probedDuration && probedDuration.src === normalizedTrackSrc) {
          resolvedDurationMs = Math.max(1, probedDuration.durationMs);
        }
      }
      if (resolvedDurationMs !== null) {
        map.set(track.layerId, Math.max(1, resolvedDurationMs + mediaDurationRevision * 0));
      }
    });
    return map;
  }, [kernel, mediaDurationVersion, probedTrackDurationsRef, tracks]);
  /* eslint-enable react-hooks/refs */
  const timelineDurationMetrics = useMemo(() => (
    resolveWorkspaceTimelineDurationMetrics({
      tracks,
      persistedDurationMs,
      pendingGenerationTracks: pendingVideoGenerationTracks,
      getMediaDurationMs: (track) => trackMediaDurationById.get(track.layerId) ?? null,
    })
  ), [pendingVideoGenerationTracks, persistedDurationMs, trackMediaDurationById, tracks]);
  const maxDeclaredTrackEndMs = timelineDurationMetrics.maxDeclaredTrackEndMs;
  const durationMs = timelineDurationMetrics.durationMs;
  const resolvedTracks = useMemo<ResolvedWorkspaceTimelineTrack[]>(() => {
    const canonicalTracks = previewTracks.map(toCanonicalTimelineTrackView);
    return resolveWorkspaceTimelineResolvedTracks(canonicalTracks, {
      durationMs,
      fallbackTrackDurationMs: Math.max(1000, durationMs),
      minTrackSpanMs: MIN_TRACK_SPAN_MS,
      roundLaneOrder: false,
      roundTiming: false,
      getMediaDurationMs: (track) => trackMediaDurationById.get(track.layerId) ?? null,
    }).map(toUiResolvedTimelineTrack);
  }, [durationMs, trackMediaDurationById, previewTracks]);
  const laneRows = useMemo<TimelineLaneRow[]>(() => (
    resolveWorkspaceTimelineLaneRows(resolvedTracks.map(toCanonicalResolvedTimelineTrackView)).map((row) => ({
      laneId: row.laneId,
      laneOrder: row.laneOrder,
      tracks: row.tracks.map(toUiResolvedTimelineTrack),
      previewLayerId: row.previewLayerId,
    }))
  ), [resolvedTracks]);
  const lanePresentationByTrackId = useMemo(() => {
    const map = new Map<string, {
      isPrimaryTrack: boolean;
      laneTracks: ResolvedWorkspaceTimelineTrack[];
      previewLayerId: string;
    }>();
    laneRows.forEach((laneRow) => {
      laneRow.tracks.forEach((laneTrack, index) => {
        map.set(laneTrack.layerId, {
          isPrimaryTrack: index === 0,
          laneTracks: laneRow.tracks,
          previewLayerId: laneRow.previewLayerId,
        });
      });
    });
    return map;
  }, [laneRows]);
  const displayResolvedTracks = useMemo(
    () => resolveWorkspaceTimelineDisplayTracks(resolvedTracks),
    [resolvedTracks]
  );
  const displayTrackOrderLayerIds = useMemo(
    () => displayResolvedTracks
      .filter((track) => lanePresentationByTrackId.get(track.layerId)?.isPrimaryTrack === true)
      .map((track) => track.layerId),
    [displayResolvedTracks, lanePresentationByTrackId]
  );
  const maxLaneOrder = useMemo(
    () => laneRows.reduce((max, laneRow) => Math.max(max, laneRow.laneOrder), Number.NEGATIVE_INFINITY),
    [laneRows]
  );
  const previewResolvedTracks = useMemo<WorkspaceTimelineTrack[]>(
    () =>
      resolvedTracks.map((track) => ({
        layerId: track.layerId,
        name: track.name,
        layerType: track.layerType,
        isLayerVisible: track.isLayerVisible,
        videoSrc: track.videoSrc,
        imageSrc: track.imageSrc,
        audioSrc: track.audioSrc,
        separatedFromLayerId: track.separatedFromLayerId ?? null,
        audioGain: track.audioGain,
        mutedInTimeline: track.mutedInTimeline,
        renderOrder: track.renderOrder,
        startMs: track.startMs,
        endMs: track.endMs,
        sourceInMs: track.sourceInMs,
        playbackRate: track.playbackRate,
        fadeInRatio: track.fadeInRatio,
        fadeOutRatio: track.fadeOutRatio,
        rotationDeg: track.rotationDeg,
        flipX: track.flipX,
        flipY: track.flipY,
        opacity: track.opacity,
        rect: track.rect,
        scale: track.scale,
      })),
    [resolvedTracks]
  );
  const visibleResolvedTracks = useMemo(
    () => resolvedTracks.filter((track) => track.isLayerVisible),
    [resolvedTracks]
  );
  const clampedCursorMs = useMemo(() => clamp(cursorMs, 0, durationMs), [cursorMs, durationMs]);
  const hasAnyTrack = resolvedTracks.length > 0;
  const hasPlayableMediaTrack = useMemo(
    () => visibleResolvedTracks.some((track) => track.layerType === 'video' || track.layerType === 'audio'),
    [visibleResolvedTracks]
  );
  const baseTimelineRange = useMemo(() => (
    resolveWorkspaceTimelineRange({
      durationMs,
      visibleResolvedTracks: visibleResolvedTracks.map(toCanonicalResolvedTimelineTrackView),
    })
  ), [durationMs, visibleResolvedTracks]);
  const playbackStartMs = baseTimelineRange.playbackStartMs;
  const playbackEndMs = baseTimelineRange.playbackEndMs;
  const rulerRangeStartMs = hasAnyTrack ? baseTimelineRange.rulerRangeStartMs : 0;
  const rulerRangeEndMs = hasAnyTrack ? baseTimelineRange.rulerRangeEndMs : Math.max(1, durationMs);
  const rulerRangeMs = Math.max(1, rulerRangeEndMs - rulerRangeStartMs);
  const timelineDropPlaceholderDurationMs = useMemo(
    // Image drops preview (and commit) as a one-frame layer; media whose true
    // duration isn't known yet still previews as a proportional placeholder.
    () => (
      timelineFileDropPreview?.layerType === 'image'
        ? MIN_TRACK_SPAN_MS
        : Math.max(1, Math.round(rulerRangeMs * TIMELINE_DROP_PLACEHOLDER_DURATION_RATIO))
    ),
    [rulerRangeMs, timelineFileDropPreview]
  );
  const timelineRenderRangeStartMs = useMemo(() => {
    let rangeStartMs = rulerRangeStartMs;
    if (trackLaneDragPreview) {
      rangeStartMs = Math.min(rangeStartMs, trackLaneDragPreview.startMs);
    }
    if (timelineFileDropPreview) {
      rangeStartMs = Math.min(rangeStartMs, timelineFileDropPreview.startMs);
    }
    pendingVideoGenerationTracks.forEach((track) => {
      rangeStartMs = Math.min(rangeStartMs, track.startMs);
    });
    return rangeStartMs;
  }, [pendingVideoGenerationTracks, rulerRangeStartMs, timelineFileDropPreview, trackLaneDragPreview]);
  const timelineRenderRangeEndMs = useMemo(() => {
    let rangeEndMs = rulerRangeEndMs;
    if (trackLaneDragPreview) {
      rangeEndMs = Math.max(
        rangeEndMs,
        trackLaneDragPreview.startMs + trackLaneDragPreview.durationMs
      );
    }
    if (timelineFileDropPreview) {
      rangeEndMs = Math.max(rangeEndMs, timelineFileDropPreview.startMs + timelineDropPlaceholderDurationMs);
    }
    pendingVideoGenerationTracks.forEach((track) => {
      rangeEndMs = Math.max(rangeEndMs, track.endMs);
    });
    return Math.max(rangeEndMs, timelineRenderRangeStartMs + 1);
  }, [
    pendingVideoGenerationTracks,
    rulerRangeEndMs,
    timelineDropPlaceholderDurationMs,
    timelineFileDropPreview,
    timelineRenderRangeStartMs,
    trackLaneDragPreview,
  ]);
  const timelineRenderRangeMs = Math.max(1, timelineRenderRangeEndMs - timelineRenderRangeStartMs);
  const activeTrackId = useMemo(() => (
    resolveWorkspaceTimelineActiveTrackId({
      cursorMs: clampedCursorMs,
      visibleResolvedTracks,
    })
  ), [clampedCursorMs, visibleResolvedTracks]);
  const splitPreviewTrackId = splitPreview?.trackId ?? null;
  const splitPreviewMsValue = splitPreview?.splitMs ?? null;
  const timelineLanePresentationRows = useMemo<WorkspaceTimelineLanePresentationRow[]>(() => {
    return displayResolvedTracks
      .map((track): WorkspaceTimelineLanePresentationRow | null => {
        const lanePresentation = lanePresentationByTrackId.get(track.layerId);
        if (!lanePresentation || !lanePresentation.isPrimaryTrack) return null;
        const isTimelineDropPreviewTrack =
          timelineFileDropPreview?.previewLayerId === lanePresentation.previewLayerId;
        const timelineDropPreviewOffsetPercent =
          isTimelineDropPreviewTrack && timelineFileDropPreview && !timelineFileDropPreview.insertAtEnd
            ? clamp(((timelineFileDropPreview.startMs - timelineRenderRangeStartMs) / Math.max(1, timelineRenderRangeMs)) * 100, 0, 100)
            : null;
        const timelineDropPreviewWidthPercent =
          timelineDropPreviewOffsetPercent === null
            ? null
            : clamp((timelineDropPlaceholderDurationMs / Math.max(1, timelineRenderRangeMs)) * 100, 0.6, 100);
        const timelineDropPreviewColor = timelineDropPreviewOffsetPercent === null
          ? null
          : timelineDropPreviewFill(timelineFileDropPreview?.layerType);
        const isTrackLaneCompactPreviewTrack =
          trackLaneDragPreview?.mode === 'compact' &&
          trackLaneDragPreview.previewLayerId === lanePresentation.previewLayerId &&
          trackLaneDragPreview.targetLaneId !== (activeTrackDragInitialLaneId ?? track.laneId);
        const trackLanePreviewOffsetPercent =
          isTrackLaneCompactPreviewTrack && trackLaneDragPreview
            ? clamp(((trackLaneDragPreview.startMs - timelineRenderRangeStartMs) / Math.max(1, timelineRenderRangeMs)) * 100, 0, 100)
            : null;
        const trackLanePreviewWidthPercent =
          isTrackLaneCompactPreviewTrack && trackLaneDragPreview
            ? clamp((trackLaneDragPreview.durationMs / Math.max(1, timelineRenderRangeMs)) * 100, 0.6, 100)
            : null;
        const laneTracks = lanePresentation.laneTracks;
        const laneLayerIds = laneTracks.map((laneTrack) => laneTrack.layerId);
        const rowActiveTrackId = activeTrackId && laneLayerIds.includes(activeTrackId) ? activeTrackId : null;
        const rowActiveDragTrackId = activeDragTrackId && laneLayerIds.includes(activeDragTrackId) ? activeDragTrackId : null;
        const rowActiveTrackOrderDragId = activeTrackOrderDragId === track.layerId ? activeTrackOrderDragId : null;
        const rowSplitHoverTrackId = splitHoverTrackId && laneLayerIds.includes(splitHoverTrackId) ? splitHoverTrackId : null;
        const rowSplitPreviewTrackId = splitPreviewTrackId && laneLayerIds.includes(splitPreviewTrackId) ? splitPreviewTrackId : null;
        const rowSplitPreviewMs = rowSplitPreviewTrackId ? splitPreviewMsValue : null;
        return {
          primaryTrack: track,
          laneTracks,
          laneLayerIds,
          previewLayerId: lanePresentation.previewLayerId,
          timelineDropPreviewOffsetPercent,
          timelineDropPreviewWidthPercent,
          timelineDropPreviewColor,
          isTrackLaneCompactPreviewTrack,
          trackLanePreviewOffsetPercent,
          trackLanePreviewWidthPercent,
          hasTrackLanePreviewConflict: isTrackLaneCompactPreviewTrack && trackLaneDragPreview?.hasConflict === true,
          activeTrackId: rowActiveTrackId,
          activeDragTrackId: rowActiveDragTrackId,
          activeTrackOrderDragId: rowActiveTrackOrderDragId,
          selectedLayerIdsSignature: buildScopedMembershipSignature(selectedLayerIdsSignature, laneLayerIds),
          separatingSoundTrackIdsSignature: buildScopedMembershipSignature(separatingSoundTrackIdsSignature, laneLayerIds),
          mergingTrackIdsSignature: buildScopedMembershipSignature(mergingTrackIdsSignature, laneLayerIds),
          splitHoverTrackId: rowSplitHoverTrackId,
          splitPreviewTrackId: rowSplitPreviewTrackId,
          splitPreviewMs: rowSplitPreviewMs,
          splitScissorPulseFrame: rowSplitHoverTrackId !== null && splitScissorPulseFrame,
          volumeOverlayTrack: null,
        };
      })
      .filter((row): row is WorkspaceTimelineLanePresentationRow => row !== null);
  }, [
    activeDragTrackId,
    activeTrackDragInitialLaneId,
    activeTrackId,
    activeTrackOrderDragId,
    displayResolvedTracks,
    lanePresentationByTrackId,
    mergingTrackIdsSignature,
    selectedLayerIdsSignature,
    separatingSoundTrackIdsSignature,
    splitHoverTrackId,
    splitPreviewMsValue,
    splitPreviewTrackId,
    splitScissorPulseFrame,
    timelineDropPlaceholderDurationMs,
    timelineFileDropPreview,
    timelineRenderRangeMs,
    timelineRenderRangeStartMs,
    trackLaneDragPreview,
  ]);

  return {
    timelineLayerIdsSignature,
    selectedLayerIdSet,
    separatingSoundTrackIdSet,
    mergingTrackIdSet,
    tracks,
    maxDeclaredTrackEndMs,
    durationMs,
    resolvedTracks,
    visibleResolvedTracks,
    previewResolvedTracks,
    laneRows,
    displayTrackOrderLayerIds,
    maxLaneOrder,
    timelineLanePresentationRows,
    hasAnyTrack,
    hasPlayableMediaLayers,
    hasPlayableMediaTrack,
    clampedCursorMs,
    playbackStartMs,
    playbackEndMs,
    rulerRangeStartMs,
    rulerRangeEndMs,
    rulerRangeMs,
    timelineDropPlaceholderDurationMs,
    timelineRenderRangeStartMs,
    timelineRenderRangeEndMs,
  };
};
